import {useAppDispatch, useAppSelector} from '../../../helpers/appReduxHelpers';
import {AnalyticsMetricsKeys} from '../../../models/api/analyticsMetricsModel';
import {AnalyticsSelectedMetricsModel} from '../../../models/api/analyticsSavedViewModel';
import {
  getCompanyHasAccountsAnalytics,
  getCompanyHasConversationStagesFeature,
} from '../../../store/data/company/companySelectors';
import {getAnalyticsReportType} from '../../../store/panels/analytics/parameters/analyticsPanelParametersSelectors';
import {fetchAnalyticsMetrics} from '../../../store/panels/analytics/report/analyticsPanelReportActions';
import {updateAnalyticsSelectedMetricsAction} from '../../../store/panels/analytics/report/analyticsPanelReportActionsWithRedirect';
import {
  getAnalyticsMetricsToFetch,
  getAnalyticsReportJobs,
  getAnalyticsSelectedMetrics,
  getConversationResolutionStrategy,
} from '../../../store/panels/analytics/report/analyticsPanelReportSelectors';
import {AnalyticsReportJobPrioritiesEnum} from '../../../store/panels/analytics/report/analyticsPanelReportState';
import {getReportBaseOptions, getReportElementMetrics} from '../reportHelpers/analyticsReportHelpers';
import {AnalyticsReportsEnum, ReportElementIdsEnum} from '../reportHelpers/reportTypes';

/**
 * Custom hook to handle metric selection for each report element.
 */
export const useAnalyticsSelectedMetrics = <T extends AnalyticsMetricsKeys>(
  reportSectionId: ReportElementIdsEnum,
) => {
  const dispatch = useAppDispatch();
  const selectedMetrics = useAppSelector(getAnalyticsSelectedMetrics);
  const jobs = useAppSelector(getAnalyticsReportJobs);
  const {hiddenMetrics} = useAppSelector(getAnalyticsMetricsToFetch);
  const reportType = useAppSelector(getAnalyticsReportType);
  const hasConversationStagesFeature = useAppSelector(getCompanyHasConversationStagesFeature);
  const hasAccountsAnalyticsFeature = useAppSelector(getCompanyHasAccountsAnalytics);
  const conversationResolutionStrategy = useAppSelector(getConversationResolutionStrategy);

  const {displayedMetrics, availableMetrics, metricsByCategory} = getReportElementMetrics<T>({
    reportType,
    reportSectionId,
    hasAccountsAnalyticsFeature,
    hasConversationStagesFeature,
    conversationResolutionStrategy,
  });

  const selectedMetricIds = selectedMetrics[reportSectionId] ?? displayedMetrics;

  const updateSelectedMetrics = (metrics: ReadonlyArray<string>, reportElementId: ReportElementIdsEnum) => {
    const payload: AnalyticsSelectedMetricsModel = {
      ...selectedMetrics,
      [reportElementId]: metrics,
    };

    dispatch(updateAnalyticsSelectedMetricsAction(payload));
  };

  // This method will potentially fetch hidden metrics, we pass the newly selected metric to check if we need to fetch them or not.
  const maybeFetchHiddenMetrics = (newlySelectedMetric: AnalyticsMetricsKeys) => {
    // Knowledge base has no hidden metrics as of right now.
    if (reportType === AnalyticsReportsEnum.KNOWLEDGE_BASE) {
      return;
    }
    // Do we already have a job containing this metric the user just selected?
    if (jobs.some((job) => job.metrics.includes(newlySelectedMetric))) {
      return;
    } // Then it's all good - it's either already fetched or being fetched.
    // Otherwise it means we need to fetch the hidden metrics.

    // Filter out "hidden" metrics but may have already have been fetched by another report element.
    const metricsToFetch = hiddenMetrics.filter(
      (metric) => !jobs.some((job) => job.metrics.includes(metric)),
    );

    if (metricsToFetch.length === 0) {
      return;
    }

    const options = getReportBaseOptions({
      reportType,
      metrics: metricsToFetch,
      hasAccountsAnalyticsFeature,
      hasConversationStagesFeature,
      conversationResolutionStrategy,
    });
    dispatch(fetchAnalyticsMetrics(metricsToFetch, AnalyticsReportJobPrioritiesEnum.SECONDARY, options));
  };

  return {
    displayedMetrics,
    selectedMetricIds: availableMetrics.filter((metricId) => selectedMetricIds.includes(metricId)), // Filtering from availableMetrics to always keep the same metric order.
    metricsByCategory,
    updateSelectedMetrics,
    maybeFetchHiddenMetrics,
  };
};
